import { reactive, computed, h } from 'vue'
import axios from 'axios'
import { createConsumer } from '@rails/actioncable'
import { useToast } from 'vue-toastification'
import { severityToneModifier } from '@/utils/notification_display'
import { computeNotificationCounts } from '@/utils/notification_metrics'
import i18n from '@/plugins/vue_i18n'

const TRAY_VISIBLE_LIMIT = 5
const TRAY_FETCH_LIMIT = 20
const BUFFER_MULTIPLIER = 3

const toast = useToast()
const state = reactive({
  items: [],
  unreadCount: 0,
  totalCount: 0,
  trayOpen: false,
  bootstrapped: false,
  bootstrapping: false,
  loading: false,
  consumer: null,
  subscription: null,
  connected: false
})
let bootstrapScheduled = false
let resizeHandler = null
let resizeListenerBound = false

const visibleNotifications = computed(() =>
  state.items.slice(0, TRAY_VISIBLE_LIMIT).filter((item) => !item.dismissed_at)
)
const hasUnread = computed(() => state.unreadCount > 0)

function removeFromState(id) {
  const index = state.items.findIndex((item) => item.id === id)
  if (index >= 0) {
    state.items.splice(index, 1)
    recomputeCounts()
  }
}

function upsert(notification) {
  if (notification.dismissed_at) {
    removeFromState(notification.id)
    return
  }
  const existingIndex = state.items.findIndex(
    (item) => item.id === notification.id
  )
  if (existingIndex >= 0) {
    state.items.splice(existingIndex, 1, {
      ...state.items[existingIndex],
      ...notification
    })
  } else {
    state.items.unshift(notification)
  }
  trimItems()
  recomputeCounts()
}

function trimItems(limit = TRAY_FETCH_LIMIT * BUFFER_MULTIPLIER) {
  if (state.items.length <= limit) return
  state.items.splice(limit)
}

function recomputeCounts(meta) {
  const { unread, total } = computeNotificationCounts(state.items, meta)
  state.unreadCount = unread
  const hasAuthoritativeTotal = meta && typeof meta.total_count === 'number'
  state.totalCount = hasAuthoritativeTotal
    ? total
    : Math.max(state.totalCount, total)
}

async function bootstrap() {
  if (state.bootstrapped || state.bootstrapping) return
  state.bootstrapping = true
  try {
    await fetchRecent()
    subscribe()
    ensureResizeListener()
    state.bootstrapped = true
  } finally {
    state.bootstrapping = false
  }
}

async function fetchRecent(options = {}) {
  const { limit = TRAY_FETCH_LIMIT } = options
  state.loading = true
  try {
    const { data } = await axios.get('/portal/notifications', {
      params: { limit }
    })
    state.items = Array.isArray(data.notifications) ? data.notifications : []
    recomputeCounts(data.meta)
  } catch (error) {
    console.error('Failed to bootstrap notifications', error)
  } finally {
    state.loading = false
  }
}

function subscribe() {
  if (state.subscription) return
  state.consumer = state.consumer || createConsumer()
  state.subscription = state.consumer.subscriptions.create(
    'NotificationChannel',
    {
      connected() {
        state.connected = true
      },
      disconnected() {
        state.connected = false
      },
      received(data) {
        const notification = data.notification || data
        if (!notification) return
        upsert(notification)
        triggerToast(notification)
      }
    }
  )
}

function triggerToast(notification) {
  if (!toast || notification.dismissed_at) return
  const type = severityToneModifier(notification.severity)
  const messageParts = [notification.title, notification.body].filter(Boolean)
  if (messageParts.length === 0) return
  const handler =
    typeof toast[type] === 'function' ? toast[type].bind(toast) : toast
  const contextUrl = resolveNotificationUrl(notification)
  const options = {
    timeout: notification.occurrences_count > 1 ? 6000 : undefined,
    type
  }
  if (contextUrl) {
    options.closeOnClick = false
  }
  const content = contextUrl
    ? buildToastContent(messageParts, () => openNotificationContext(contextUrl))
    : messageParts.join('\n')
  handler(content, options)
}

function pushLocalNotification({
  title,
  body = '',
  severity = 'info',
  context = {}
}) {
  const notification = {
    id: `local-${crypto.randomUUID ? crypto.randomUUID() : Date.now()}`,
    title,
    body,
    severity,
    context,
    delivered_channels: ['portal_toast'],
    occurrences_count: 1,
    created_at_iso: new Date().toISOString(),
    toast_sent_at: new Date().toISOString(),
    read_at: null,
    dismissed_at: null
  }
  upsert(notification)
  triggerToast(notification)
  return notification
}

async function dismiss(id) {
  const notification = await mutateNotification(id, { dismiss: true })
  if (notification) {
    upsert(notification)
  } else {
    removeFromState(id)
  }
}

async function markRead(id) {
  const notification = await mutateNotification(id, { read: true })
  if (notification) {
    upsert(notification)
  }
}

async function mutateNotification(id, attrs) {
  if (!id) return null
  try {
    const { data } = await axios.patch(`/portal/notifications/${id}`, {
      notification: attrs
    })
    return data.notification || data || null
  } catch (error) {
    console.error('Failed to mutate notification', error)
    throw error
  }
}

function toggleTray(forceValue = null) {
  const nextOpen = forceValue === null ? !state.trayOpen : Boolean(forceValue)
  if (nextOpen === state.trayOpen) return
  state.trayOpen = nextOpen
  if (nextOpen) {
    fetchRecent()
  }
}

function clearLocal() {
  state.items = []
  state.unreadCount = 0
  state.totalCount = 0
  state.bootstrapped = false
  state.trayOpen = false
  if (state.subscription) {
    state.subscription.unsubscribe()
    state.subscription = null
  }
  if (state.consumer) {
    state.consumer.disconnect()
    state.consumer = null
  }
  teardownResizeListener()
}

function syncFromHub(notification) {
  if (!notification) return
  upsert(notification)
}

function ensureBootstrapScheduled() {
  if (bootstrapScheduled || typeof window === 'undefined') return
  bootstrapScheduled = true
  const runner = () => bootstrap()
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(runner)
  } else {
    Promise.resolve().then(runner)
  }
}

function ensureResizeListener() {
  if (resizeListenerBound || typeof window === 'undefined') return
  resizeHandler = () => toggleTray(false)
  window.addEventListener('resize', resizeHandler)
  resizeListenerBound = true
}

function teardownResizeListener() {
  if (!resizeListenerBound || typeof window === 'undefined') return
  window.removeEventListener('resize', resizeHandler)
  resizeListenerBound = false
  resizeHandler = null
}

function resolveNotificationUrl(notification) {
  const context = notification.context || {}
  return context.url || context.path || null
}

function openNotificationContext(url) {
  if (!url) return
  if (/^https?:\/\//i.test(url)) {
    window.open(url, '_blank', 'noopener')
    return
  }
  window.location.assign(url)
}

function buildToastContent(messageParts, onView) {
  const viewLabel = translate('notifications.admin.tray.view', 'View')
  return h('div', { class: 'notification-toast' }, [
    h(
      'div',
      { class: 'notification-toast__text' },
      messageParts.map((part, index) =>
        h('span', { key: `toast-line-${index}` }, part)
      )
    ),
    h(
      'button',
      {
        class: 'notification-toast__action',
        type: 'button',
        onClick: (event) => {
          event.stopPropagation()
          onView()
        }
      },
      viewLabel
    )
  ])
}

function translate(key, fallback) {
  try {
    const translator =
      i18n && i18n.global && typeof i18n.global.t === 'function'
        ? i18n.global.t
        : null
    if (translator) {
      const translation = translator(key)
      if (translation && translation !== key) {
        return translation
      }
    }
  } catch (error) {
    console.warn('Failed to translate toast label', error)
  }
  return fallback
}

export function useNotificationStore() {
  ensureBootstrapScheduled()
  return {
    state,
    visibleNotifications,
    hasUnread,
    bootstrap,
    fetchRecent,
    dismiss,
    markRead,
    toggleTray,
    pushLocalNotification,
    clearLocal,
    recomputeCounts,
    syncFromHub
  }
}

export default {
  useNotificationStore
}
